import { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, GripVertical, Loader2, RefreshCw, Check } from 'lucide-react';
import { interfacesService, type ErpWarehouse } from '@/lib/interfacesService';
import type { WarehousePref } from '@/lib/interfacesCatalog';

export function useErpWarehouses(companyId: string, enabled: boolean) {
  const [list, setList] = useState<ErpWarehouse[]>([]);
  const [at, setAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setRefreshing(true); setError(null);
    const res = await interfacesService.refreshWarehouses(companyId);
    setRefreshing(false);
    if (res.ok) { setList(res.data); setAt(new Date().toISOString()); }
    else setError(res.error);
  }, [companyId]);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    interfacesService.getWarehouses(companyId)
      .then(r => {
        if (!alive) return;
        setList(r.list); setAt(r.at); setLoading(false);
        if (!r.list.length) void refresh();
      })
      .catch(() => { if (alive) { setLoading(false); setError('לא הצלחנו לטעון את רשימת המחסנים'); } });
    return () => { alive = false; };
  }, [companyId, enabled, refresh]);

  return { list, at, loading, refreshing, error, refresh };
}

export type ErpWarehousesState = ReturnType<typeof useErpWarehouses>;

function RefreshBar({ state, disabled }: { state: ErpWarehousesState; disabled: boolean }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-3">
      <span className="text-xs text-gray-500">
        {state.at ? `עודכן ${new Date(state.at).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' })}` : 'הרשימה עוד לא נטענה מרווחית'}
      </span>
      <button
        type="button"
        onClick={state.refresh}
        disabled={disabled || state.refreshing}
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${state.refreshing ? 'animate-spin' : ''}`} />
        רענון רשימה
      </button>
    </div>
  );
}

function Status({ state }: { state: ErpWarehousesState }) {
  if (state.error) return <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{state.error}</p>;
  if (state.loading || (state.refreshing && !state.list.length)) {
    return <p className="flex items-center gap-2 py-3 text-xs text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> טוען מחסנים מרווחית...</p>;
  }
  return null;
}

function mergePrefs(saved: WarehousePref[], list: ErpWarehouse[]): WarehousePref[] {
  if (!list.length) return saved;
  const byId = new Map(list.map(w => [w.id, w]));
  const kept = saved.filter(p => byId.has(p.id)).map(p => ({ ...p, name: byId.get(p.id)!.name }));
  const added = list.filter(w => !kept.some(p => p.id === w.id)).map(w => ({ id: w.id, name: w.name, enabled: true }));
  return [...kept, ...added];
}

const samePrefs = (a: WarehousePref[], b: WarehousePref[]) =>
  a.length === b.length && a.every((p, i) => p.id === b[i]?.id && p.enabled === b[i]?.enabled && p.name === b[i]?.name);

export function WarehousePriorityField({ state, value, disabled, onChange }: {
  state: ErpWarehousesState;
  value: WarehousePref[];
  disabled: boolean;
  onChange: (v: WarehousePref[]) => void;
}) {
  const prefs = mergePrefs(value, state.list);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!disabled && !samePrefs(prefs, value)) onChange(prefs);
  }, [prefs, value, disabled, onChange]);

  const move = (from: number, to: number) => {
    if (to < 0 || to >= prefs.length || from === to) return;
    const next = [...prefs];
    const [item] = next.splice(from, 1);
    if (!item) return;
    next.splice(to, 0, item);
    onChange(next);
  };
  let rank = 0;

  return (
    <div>
      <RefreshBar state={state} disabled={disabled} />
      <Status state={state} />
      {!!prefs.length && (
        <ol className="space-y-2">
          {prefs.map((p, i) => {
            if (p.enabled) rank++;
            return (
              <li
                key={p.id}
                draggable={!disabled}
                onDragStart={() => setDragIndex(i)}
                onDragOver={e => { e.preventDefault(); if (dragIndex !== null && dragIndex !== i) { move(dragIndex, i); setDragIndex(i); } }}
                onDragEnd={() => setDragIndex(null)}
                className={`flex items-center gap-3 rounded-lg border bg-white px-3 py-2 transition-all ${
                  dragIndex === i ? 'border-sky-400 shadow-md' : 'border-gray-200'
                } ${p.enabled ? '' : 'opacity-60'}`}
              >
                <GripVertical className={`h-4 w-4 shrink-0 text-gray-400 ${disabled ? '' : 'cursor-grab'}`} />
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                  p.enabled ? 'bg-sky-100 text-sky-800' : 'bg-gray-100 text-gray-400'
                }`}>
                  {p.enabled ? rank : '-'}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800">
                  {p.name} <span className="text-xs font-normal text-gray-400">#{p.id}</span>
                </span>
                <div className="flex items-center gap-1">
                  <button type="button" disabled={disabled || i === 0} onClick={() => move(i, i - 1)} aria-label="העלה" className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30">
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button type="button" disabled={disabled || i === prefs.length - 1} onClick={() => move(i, i + 1)} aria-label="הורד" className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30">
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange(prefs.map(x => (x.id === p.id ? { ...x, enabled: !x.enabled } : x)))}
                    className={`mr-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                      p.enabled ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {p.enabled ? 'פעיל' : 'לא בשימוש'}
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {!state.loading && !state.refreshing && !prefs.length && !state.error && (
        <p className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">לא נמצאו מחסנים ברווחית. המסמכים יופקו במחסן ברירת המחדל.</p>
      )}
    </div>
  );
}

export function WarehouseMultiField({ state, value, disabled, onChange }: {
  state: ErpWarehousesState;
  value: number[];
  disabled: boolean;
  onChange: (v: number[]) => void;
}) {
  const selected = new Set(value);
  const toggle = (id: number) => onChange(selected.has(id) ? value.filter(v => v !== id) : [...value, id]);

  return (
    <div>
      <RefreshBar state={state} disabled={disabled} />
      <Status state={state} />
      <div className="grid gap-2 sm:grid-cols-2">
        {state.list.map(w => {
          const on = selected.has(w.id);
          return (
            <button
              key={w.id}
              type="button"
              disabled={disabled}
              onClick={() => toggle(w.id)}
              className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-right text-sm transition-all ${
                on ? 'border-sky-500 bg-sky-50 text-sky-800' : 'border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? 'border-sky-600 bg-sky-600' : 'border-gray-300 bg-white'}`}>
                {on && <Check className="h-3 w-3 text-white" />}
              </span>
              <span className="truncate font-medium">{w.name}</span>
            </button>
          );
        })}
      </div>
      {!!state.list.length && (
        <p className="mt-2 text-xs text-gray-500">
          {value.length ? `נבחרו ${value.length} מחסנים` : 'לא נבחר מחסן — המלאי יסוכם מכל המחסנים'}
        </p>
      )}
    </div>
  );
}
